import { useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import Iconify from '@/components/media/iconify';
import { useOrderList } from '@/hooks/lists/use-order-list';
import CustomScreenList from '@/components/layout/custom-screen/custom-screen-list';
import FiltersHub, { FilterField, FilterHubResult } from '@/components/forms/filters-hub';
import type { BreadcrumbsLinkProps } from '@/components/layout/custom-breadcrumbs';
import { OrderTypes, ColumnsOrder, ChoicesOrderStatus } from '@/types';
import { useResponsive } from '@/hooks';
import { OrderTableRow } from '../components/order-table-row';
import { OrdersExport } from '../dialogs/dialog-export-order';
import AccountingOrdersExport from '../dialogs/accounting-export-order';
import OrderFilterResult from '../components/order-filter-result';

type Props = {
  setTotal?: (total: number) => void;
  organizationId?: string;
  orderType?: OrderTypes;
  standalone?: boolean; // When true, renders as standalone component without container
  noContainer?: boolean; // When true, renders without Container and breadcrumbs
  // Props for main page view
  heading?: string;
  links?: BreadcrumbsLinkProps[];
  action?: React.ReactNode;
};

export function OrderListEnhanced({
  setTotal,
  organizationId,
  orderType = OrderTypes.PURCHASE_ORDER,
  standalone = false,
  noContainer = false,
  heading = '',
  links = [],
  action,
}: Props) {
  const { t } = useTranslation();

  // Use the order-specific hook - all hooks are now centralized here
  const {
    filters,
    page,
    rowsPerPage,
    canReset,

    // Data
    data,

    // UI state
    openExport,
    openAccountingExport,

    getOrderPermissions,
    orderPermissions,

    // Handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleFilterSearch,
    handleFilters,
    handleResetFilters,
    handleSortRequest,
    handleOpenExport,
    handleCloseExport,
    handleOpenAccountingExport,
    handleCloseAccountingExport,

    // Actions
    handleEditRow,
    handleDeleteRow,
    handleBill,
  } = useOrderList({ organizationId, orderType });

  const isUnderLargeScreen = useResponsive('down', 'lg');

  // Update total when data changes
  useEffect(() => {
    if (setTotal) {
      setTotal(data?.total ?? 0);
    }
  }, [data?.total, setTotal]);

  // Filter fields configuration - matching OrderList exactly
  const fields: FilterField[] = useMemo(
    () => [
      ...(organizationId !== undefined
        ? []
        : [
            {
              type: 'autocomplete' as const,
              name: 'organizations',
              label: t('Organizations'),
              url: 'organizations/all',
            },
            {
              type: 'autocomplete' as const,
              name: 'owners',
              label: t('Sales Executive'),
              url: 'users/staff',
              showLogo: true,
              optionsMapper: (staffUsers: any) =>
                staffUsers.map((staff: any) => ({
                  ...staff,
                  label: `${staff.firstName} ${staff.lastName}`,
                })),
            },
          ]),
      {
        type: 'search-text',
        name: 'salesOrderNumber',
        label: t('Sales Order Number'),
      },
      {
        type: 'autocomplete',
        name: 'status',
        label: t('Status'),
        options: [
          {
            _id: ChoicesOrderStatus.created,
            label: t('Draft'),
          },
          {
            _id: ChoicesOrderStatus.pendingApproval,
            label: t('Pending Approval'),
          },
          {
            _id: ChoicesOrderStatus.pendingProvision,
            label: t('Pending Provision'),
          },
          {
            _id: ChoicesOrderStatus.provisioning,
            label: t('Provisioning'),
          },
          {
            _id: ChoicesOrderStatus.failedProvision,
            label: t('Failed Provision'),
          },
          {
            _id: ChoicesOrderStatus.completed,
            label: t('Completed'),
          },
          {
            _id: ChoicesOrderStatus.cancelled,
            label: t('Cancelled'),
          },
          {
            _id: ChoicesOrderStatus.rejected,
            label: t('Rejected'),
          },
        ],
      },
      {
        type: 'checkbox',
        name: 'pendingBilling',
        label: t('Pending Billing'),
      },
      {
        type: 'checkbox',
        name: 'isMarket',
        label: t('Marketplace'),
      },
    ],
    [organizationId, t]
  );

  // Table headers configuration
  const tableHeaders = useMemo(
    () => [
      {
        id: 'orderNumber',
        label: t('Order Nº'),
        align: 'left',
        width: '120px',
        sortable: true,
        sortKey: ColumnsOrder.orderNumber,
      },
      {
        id: 'company',
        label: t('Company'),
        align: 'left',
        width: '200px',
        sortable: true,
        sortKey: ColumnsOrder.company,
      },
      ...(isUnderLargeScreen
        ? []
        : [
            {
              id: 'items',
              label: t('Items'),
              align: 'center',
              width: '80px',
              sortable: true,
              sortKey: ColumnsOrder.items,
            },
          ]),
      {
        id: 'billingStatus',
        label: t('Billing Status'),
        align: 'center',
        width: '120px',
      },
      {
        id: 'submittedBy',
        label: t('Submitted by'),
        align: 'left',
        width: '280px',
        sortable: true,
        sortKey: ColumnsOrder.submittedBy,
      },
      {
        id: 'submittedAt',
        label: t('Submitted at'),
        align: 'left',
        width: '130px',
        sortable: true,
        sortKey: ColumnsOrder.submittedAt,
      },
      {
        id: 'totalAmount',
        label: t('Total Import'),
        align: 'center',
        width: '100px',
        sortable: false,
      },
      {
        id: 'status',
        label: t('Status'),
        align: 'center',
        width: '100px',
        sortable: true,
        sortKey: ColumnsOrder.status,
      },
      ...(orderPermissions.canView || orderPermissions.canUpdate || orderPermissions.canDelete
        ? [
            {
              id: 'actions',
              label: '',
              align: 'right',
              width: '20px',
            },
          ]
        : []),
    ],
    [isUnderLargeScreen, orderPermissions, t]
  );

  // Memoize the table rows to improve performance
  const tableRows = useMemo(() => {
    if (!data?.docs) return null;

    return data.docs.map((row: any) => (
      <OrderTableRow
        key={row._id}
        row={row}
        selected={false}
        onDeleteRow={() => handleDeleteRow(row._id)}
        onEditRow={() => handleEditRow(row._id)}
        onBill={() => handleBill(row._id)}
        userPermissions={getOrderPermissions(row)}
        isUnderLargeScreen={isUnderLargeScreen}
      />
    ));
  }, [
    data?.docs,
    handleDeleteRow,
    handleEditRow,
    handleBill,
    getOrderPermissions,
    isUnderLargeScreen,
  ]);

  // Create adapter for FiltersHub
  const handleFiltersHubChange = useMemo(
    () => (newFilters: any) => {
      if (typeof newFilters === 'function') {
        const updatedFilters = newFilters(filters);
        Object.keys(updatedFilters).forEach((key) => {
          if ((updatedFilters as any)[key] !== (filters as any)[key]) {
            handleFilters(key, (updatedFilters as any)[key]);
          }
        });
      } else {
        Object.keys(newFilters).forEach((key) => {
          if ((newFilters as any)[key] !== (filters as any)[key]) {
            handleFilters(key, (newFilters as any)[key]);
          }
        });
      }
    },
    [handleFilters, filters]
  );

  // Memoize the filters result component
  const filtersResult = useMemo(
    () => (
      <FilterHubResult
        filters={filters}
        fields={fields}
        canReset={canReset}
        onChange={handleFiltersHubChange}
        onResetFilters={handleResetFilters}
        results={data?.total ?? 0}
      />
    ),
    [filters, fields, canReset, handleFiltersHubChange, handleResetFilters, data?.total]
  );

  // Memoize the table filters component
  const tableFiltersComponent = useMemo(
    () => (
      <FiltersHub
        filters={filters}
        onChange={handleFiltersHubChange}
        fields={fields}
        canReset={canReset}
        onResetFilters={handleResetFilters}
      />
    ),
    [filters, handleFiltersHubChange, fields, canReset, handleResetFilters]
  );

  // Create menu options function with useCallback
  const menuOptions = useCallback(
    (popover: any) => {
      const { onClose } = popover;
      return (
        <>
          <MenuItem
            onClick={() => {
              handleOpenExport();
              onClose();
            }}
          >
            <ListItemIcon sx={{ mr: 0 }}>
              <Iconify icon="solar:export-bold" />
            </ListItemIcon>
            <ListItemText>{t('Export')}</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleOpenAccountingExport();
              onClose();
            }}
          >
            <ListItemIcon sx={{ mr: 0 }}>
              <Iconify icon="mdi:finance" />
            </ListItemIcon>
            <ListItemText>{t('Accounting report')}</ListItemText>
          </MenuItem>
        </>
      );
    },
    [handleOpenExport, handleOpenAccountingExport, t]
  );

  return (
    <CustomScreenList
      standalone={standalone}
      noContainer={noContainer}
      heading={heading}
      links={links}
      action={action}
      isLoading={data === undefined}
      handleChangePage={handleChangePage}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      rowsPerPage={rowsPerPage}
      page={page}
      data={data}
      results={filtersResult}
      tableHeaders={tableHeaders}
      rowRenderer={tableRows}
      handleFilterSearch={handleFilterSearch}
      filters={filters}
      handleFilters={handleFilters}
      handleSortRequest={handleSortRequest}
      tableFilters={tableFiltersComponent}
      options={menuOptions}
    >
      <OrdersExport title={t('Export')} open={openExport} onClose={handleCloseExport} />
      <AccountingOrdersExport
        title={t('Accounting Export')}
        open={openAccountingExport}
        onClose={handleCloseAccountingExport}
      />
    </CustomScreenList>
  );
}
